/**
 * QuickDraftMenu — a standalone dropdown offering three popular drafting
 * shortcuts: email, note, task. Deliberately reusable: everything it needs
 * (compose panels, create permissions, the default task stage) comes from
 * context through the shared compose hooks, so any surface can drop this in
 * with a one-line import. Never imports from ProposalListPanel or
 * any actions-list module — it must not depend on the AI Actions list's
 * state or route context to stay portable to other surfaces.
 *
 * Currently lives under `components/proposals/` because the AI Actions list
 * toolbar is its first (and so far only) consumer, and this is the path the
 * phase spec's test file lives under. The component itself has zero
 * proposals-specific imports, so moving it to `lib/components/` later is a
 * one-line import change, not a rewrite.
 */

import { ClipboardCheck, File02, Mail01, Plus } from '@untitledui/icons';
import { type FC, useCallback, useMemo } from 'react';
import type { Key } from 'react-aria-components';

import { useEmailCompose } from '@/contexts/EmailComposeContext';
import { Button } from '@/lib/base/buttons/button';
import { Dropdown } from '@/lib/base/overlays/dropdown';
import { useOpenNoteCompose } from '@/lib/crm/record/notes/useNoteTabActions';
import { useOpenTaskCompose } from '@/lib/crm/record/task/useOpenTaskCompose';

export interface QuickDraftMenuProps {
  /**
   * Retained for API stability with the toolbar consumers — the composer
   * resolves org context itself. Unused inside.
   */
  orgId: number;
  /** Extra classes forwarded to the trigger button. */
  className?: string;
}

type QuickDraftItemId = 'email' | 'note' | 'task';

interface QuickDraftItem {
  id: QuickDraftItemId;
  label: string;
  icon: FC<{ className?: string }>;
}

// Hoisted to module scope — this list never depends on props/state
// (FRONTEND_PATTERNS.md "Hoist Constants Out of Components").
const QUICK_DRAFT_ITEMS: QuickDraftItem[] = [
  { id: 'email', label: 'Draft email', icon: Mail01 },
  { id: 'note', label: 'Draft note', icon: File02 },
  // No "Draft reminder": notes no longer carry a due — a dated to-do is a
  // task, and "Draft task" below already covers it.
  { id: 'task', label: 'Draft task', icon: ClipboardCheck },
];

export function QuickDraftMenu({ className }: QuickDraftMenuProps) {
  const { openEmailCompose } = useEmailCompose();
  // The note create gate lives in useOpenNoteCompose, so "Draft note" is hidden
  // for a user who cannot create notes — mirroring how "Draft task" is gated.
  const { openNoteCompose, canCreateNote } = useOpenNoteCompose();
  // Stage resolution and the create gate both live in useOpenTaskCompose, so
  // "Draft task" prefers "To Do" and stays available on a stage-less org —
  // exactly like the calendar's "Add task" and a record's Tasks tab.
  const { openTaskCompose, canCreateTask, isStageLoading } =
    useOpenTaskCompose();

  const items = useMemo(
    () =>
      QUICK_DRAFT_ITEMS.filter(item => {
        if (item.id === 'task') {
          return canCreateTask;
        }
        if (item.id === 'note') {
          return canCreateNote;
        }
        return true;
      }),
    [canCreateTask, canCreateNote]
  );

  // Keep "Draft task" pending (disabled) while the default stage is still
  // resolving: opening then would treat the transient unresolved stage like a
  // stage-less org and create an UNstaged task, even though "To Do" resolves a
  // moment later. Once loading finishes it enables — including on a genuinely
  // stage-less org (isStageLoading is false there), which drafts unstaged.
  const disabledKeys = useMemo<QuickDraftItemId[]>(
    () => (isStageLoading ? ['task'] : []),
    [isStageLoading]
  );

  const handleMenuAction = useCallback(
    (key: Key) => {
      switch (String(key) as QuickDraftItemId) {
        case 'email':
          openEmailCompose();
          return;
        case 'note':
          openNoteCompose();
          return;
        case 'task':
          // Belt-and-suspenders: disabledKeys already suppresses onAction for a
          // disabled item, but guard here too so a stage still loading can never
          // open an unstaged task.
          if (isStageLoading) {
            return;
          }
          openTaskCompose();
          return;
      }
    },
    [openEmailCompose, openNoteCompose, openTaskCompose, isStageLoading]
  );

  return (
    <Dropdown.Root instance='quick-draft'>
      <Button
        color='tertiary'
        size='sm'
        aria-label='Quick draft'
        className={className}
        iconLeading={<Plus aria-hidden='true' className='size-4' />}
      >
        Quick draft
      </Button>
      <Dropdown.Popover placement='bottom right'>
        {/* selectionMode='none': these are one-shot commands, not a picker
            with persistent selection state (unlike the view-switcher menu),
            so items render as `menuitem`, not `menuitemradio`. */}
        <Dropdown.Menu
          selectionMode='none'
          disabledKeys={disabledKeys}
          onAction={handleMenuAction}
        >
          {items.map(item => (
            <Dropdown.Item
              key={item.id}
              id={item.id}
              label={item.label}
              icon={item.icon}
              // While the task stage is still resolving, "Draft task" is
              // disabled (see disabledKeys); explain why so opening the menu
              // and clicking it doesn't read as a dead control.
              disabledTooltip={
                item.id === 'task' && isStageLoading
                  ? 'Loading task stages…'
                  : undefined
              }
            />
          ))}
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown.Root>
  );
}
